import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import message from 'antd/lib/message';
import Model from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Upload from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react';
import { axiosGetCall, axiosPostCall } from '../../api/axiosCall';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import { uploadFile, S3ImageFolder } from '../../utils/imageUploader';

const { Option } = Select;

function AddSparePart({ isOpen, callback, handleAddModelClose }) {
  const [addForm] = Form.useForm();
  const { token } = useContext(AuthContext);

  const [fileList, setFileList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [productList, setProductList] = useState([]);

  useEffect(() => {
    getProductList();
  }, []);

  async function getProductList() {
    try {
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_ALL_PRODUCT,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.hideErrorFromUser) {
        setProductList(res.data);
      } else {
        message.error(res.error.message);
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  function closeModel() {
    handleAddModelClose();
    addForm.resetFields();
    setFileList([]);
  }

  async function addProduct() {
    let formValues;
    try {
      formValues = await addForm.validateFields();
    } catch (err) {
      // Show first validation error and stop
      const firstError = err?.errorFields?.[0];
      if (firstError) {
        message.error(
          firstError.errors?.[0] || 'Please fill all required fields',
        );
        try {
          addForm.scrollToField(firstError.name);
        } catch (_) {}
      }
      return;
    }
    const { sparePartName, modelNo, gstTax, P1, P2, P3, R1, productName } =
      formValues;
    try {
      setIsLoading(true);
      const list = [];

      if (!fileList.length) {
        message.error('At least one image is required');
        setIsLoading(false);
        return;
      }

      for (const file of fileList) {
        const data = await uploadFile(file, S3ImageFolder.SubPart, token);
        list.push(data);
      }

      const productData = productList
        .filter((item) => {
          if (productName.includes(item.productName)) return item;
        })
        .map((temp) => {
          return temp.productId;
        });

      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.ADD_SPARE_PART,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          productSparePartName: sparePartName,
          psModelNo: modelNo,
          productIds: productData,
          prices: {
            P1,
            P2,
            P3,
            R1,
          },
          userName: 'string',
          gstTax: gstTax,
          filesPaths: list[0],
        },
      });
      if (res?.hideErrorFromUser) {
        message.success(res.data.message);
        callback();
        closeModel();
        setIsLoading(false);
        return;
      } else {
        message.error(res?.error?.message);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      if (fileList.length < 1) {
        setFileList([...fileList, file]);
      } else {
        message.error('Max image upload limit reached');
      }
      return false;
    },
    fileList,
  };

  return (
    <div>
      <Model
        open={isOpen}
        onCancel={closeModel}
        closable={false}
        footer={null}
      >
        <div className='flex flex-col space-y-4'>
          <div className='font-bold text-2xl'>Add Spare Part</div>
          <Form form={addForm}>
            <Form.Item
              label='Spare Part Name'
              name='sparePartName'
              rules={[
                { required: true, message: 'Please enter spare part name' },
              ]}
            >
              <Input name='sparePartName' />
            </Form.Item>
            <Form.Item
              label='Model Number'
              name='modelNo'
              rules={[{ required: true, message: 'Please enter model number' }]}
            >
              <Input name='modelNo' />
            </Form.Item>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='GST'
                name='gstTax'
                rules={[{ required: true, message: 'Please enter GST' }]}
              >
                <InputNumber name='gstTax' />
              </Form.Item>
            </Row>
            <span className='py-1 font-bold text-lg'>Prices:</span>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P1'
                name='P1'
                rules={[{ required: true, message: 'Please enter P1 price' }]}
              >
                <InputNumber name='P1' />
              </Form.Item>
              <Form.Item
                label='P2'
                name='P2'
                rules={[{ required: true, message: 'Please enter P2 price' }]}
              >
                <InputNumber name='P2' />
              </Form.Item>
            </Row>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P3'
                name='P3'
                rules={[{ required: true, message: 'Please enter P3 price' }]}
              >
                <InputNumber name='P3' />
              </Form.Item>
              <Form.Item
                label='R1'
                name='R1'
                rules={[{ required: true, message: 'Please enter R1 price' }]}
              >
                <InputNumber name='R1' />
              </Form.Item>
            </Row>
            <Form.Item
              label='Product'
              name='productName'
              rules={[
                {
                  required: true,
                  type: 'array',
                  message: 'Please select at least one product',
                },
              ]}
            >
              <Select
                placeholder='Select a Product'
                showSearch
                mode='multiple'
              >
                {productList.map((product) => (
                  <Option
                    key={product.productId}
                    value={product.productName}
                  >
                    {product.productName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label='Upload'
              valuePropName='fileList'
              getValueFromEvent={normFile}
              rules={[
                {
                  validator: (_, value) => {
                    if (value && value.length > 0) return Promise.resolve();
                    return Promise.reject(
                      new Error('At least one image is required'),
                    );
                  },
                },
              ]}
            >
              <Upload
                accept='image/*'
                {...uploadProps}
                multiple={false}
                maxCount={1}
              >
                <Button
                  type='dashed'
                  icon={<UploadOutlined />}
                  className='border-primary text-primary'
                >
                  Click to Upload
                </Button>
              </Upload>
            </Form.Item>
          </Form>
          <div className='flex flex-row justify-around'>
            <Button
              onClick={closeModel}
              disabled={isLoading}
              className='w-28 bg-surface border border-border text-text hover:text-primary hover:border-primary'
            >
              Close
            </Button>
            <Button
              onClick={addProduct}
              disabled={isLoading}
              type='primary'
              className='w-36 border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
            >
              Add
            </Button>
          </div>
        </div>
      </Model>
    </div>
  );
}

export default AddSparePart;
